import { useState } from 'react';
import { User, Phone, Trash2, Shield, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { User as UserType } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { UpdatePhoneDialog } from './UpdatePhoneDialog';
import { DeleteUserDialog } from './DeleteUserDialog';
import { PasswordDialog } from './PasswordDialog';

interface UserItemProps {
  user: UserType;
  isAdmin: boolean;
  onUpdatePhone: (userId: string, phoneNumber: string) => Promise<void>;
  onUpdatePassword: (userId: string, password: string) => Promise<void>;
  onDelete: (userId: string) => Promise<void>;
}

export function UserItem({
  user,
  isAdmin,
  onUpdatePhone,
  onUpdatePassword,
  onDelete,
}: UserItemProps) {
  const currentUser = useAuthStore((state) => state.user);
  const [showUpdatePhone, setShowUpdatePhone] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = currentUser?.id === user.id;

  const handleUpdatePhone = async (phoneNumber: string) => {
    try {
      setError(null);
      await onUpdatePhone(user.id, phoneNumber);
      setShowUpdatePhone(false);
    } catch (error) {
      setError('Failed to update phone number');
    }
  };

  const handleUpdatePassword = async (password: string) => {
    try {
      setError(null);
      await onUpdatePassword(user.id, password);
      setShowPasswordDialog(false);
    } catch (error) {
      setError('Failed to update password');
    }
  };

  const handleDelete = async () => {
    try {
      setError(null);
      await onDelete(user.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      setError('Failed to delete user');
    }
  };

  return (
    <div className="p-3 md:p-4 flex flex-col gap-4 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* User Info Section */}
        <div className="flex items-start sm:items-center space-x-3 rtl:space-x-reverse">
          <span className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            {user.role === 'admin' ? (
              <Shield className="h-5 w-5 text-primary" />
            ) : (
              <User className="h-5 w-5 text-primary" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                {user.username}
              </h3>
              {user.role === 'admin' && (
                <span className="inline-flex text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                  مدير
                </span>
              )}
              {isSelf && (
                <span className="inline-flex text-xs bg-secondary/10 text-secondary px-2 py-1 rounded-full">
                  أنت
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {user.phoneNumber || 'لا يوجد رقم هاتف'}
            </p>
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
        </div>

        {/* Actions Section */}
        {isAdmin && (
          <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none hover:bg-secondary hover:text-white"
              onClick={() => setShowPasswordDialog(true)}
            >
              <Key className="h-4 w-4 ml-2" />
              <span className="whitespace-nowrap">تعيين كلمة المرور</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none hover:bg-secondary hover:text-white"
              onClick={() => setShowUpdatePhone(true)}
            >
              <Phone className="h-4 w-4 ml-2" />
              <span className="whitespace-nowrap">تحديث رقم الهاتف</span>
            </Button>
            {!isSelf && (
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      <UpdatePhoneDialog
        open={showUpdatePhone}
        onClose={() => setShowUpdatePhone(false)}
        onSubmit={handleUpdatePhone}
        initialPhone={user.phoneNumber}
      />

      <DeleteUserDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />

      <PasswordDialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        onSubmit={handleUpdatePassword}
        username={user.username}
      />
    </div>
  );
}